import { useEffect, useState } from "react";
import { useAuthStore } from "../../../Stores/AuthStore";
import { useLocation, useNavigate } from "react-router-dom";
import { signIn } from "../../../Api/auth";
import { publicApi } from "../../../Api/api";

// Rôles autorisés pour gestion-sav
const ALLOWED_ROLES = ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_SUPERVISOR', 'ROLE_COMMERCIAL'];

const hasAllowedRole = (roles: string[] | undefined): boolean => {
  return roles?.some(role => ALLOWED_ROLES.includes(role)) ?? false;
};

export default function Login() {
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuth, user } = useAuthStore();

  // Si déjà connecté avec les bons droits, rediriger vers l'accueil
  useEffect(() => {
    if (user && user.isActive && hasAllowedRole(user.roles)) {
      navigate(location.state?.from?.pathname || "/", { replace: true });
    }
  }, [user, navigate, location.state?.from?.pathname]);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const result = await signIn({ email, password });
      if (result.data.user && result.data.access_token) {
        const user = result.data.user;

        // Vérifier si l'utilisateur est actif
        if (!user.isActive) {
          setError("Votre compte a été désactivé. Veuillez contacter un administrateur.");
          setIsLoading(false);
          return;
        }

        // Vérifier si l'utilisateur a un rôle autorisé
        if (!hasAllowedRole(user.roles)) {
          setError("Vous n'avez pas les droits d'accès à cette application.");
          setIsLoading(false);
          return;
        }

        setAuth(result.data.access_token, user);
        navigate(location.state?.from?.pathname || "/", { replace: true });
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || "Identifiants incorrects");
    } finally {
      setIsLoading(false);
    }
  };

  // Afficher les erreurs passées par RequireAuth
  useEffect(() => {
    if (location.state?.error) {
      setError(location.state.error);
    }
  }, [location.state?.error]);

  // Gestion du magic link
  useEffect(() => {
    if (location.state?.access_token) {
      setIsLoading(true);
      (async () => {
        try {
          const result = await publicApi.get("/auth/profile", {
            headers: {
              Authorization: `Bearer ${location.state?.access_token}`
            }
          });
          const user = result.data;

          // Vérifier si l'utilisateur est actif
          if (!user.isActive) {
            setError("Votre compte a été désactivé. Veuillez contacter un administrateur.");
            setIsLoading(false);
            return;
          }

          // Vérifier si l'utilisateur a un rôle autorisé
          if (!hasAllowedRole(user.roles)) {
            setError("Vous n'avez pas les droits d'accès à cette application.");
            setIsLoading(false);
            return;
          }

          setAuth(location.state?.access_token, user);
          navigate(location.state?.from?.pathname || "/", { replace: true });
        } catch (err) {
          setError("Le lien de connexion est invalide ou expiré");
          setIsLoading(false);
        }
      })();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.access_token]);

  if (isLoading) {
    return (
      <div className="tw-min-h-screen tw-bg-gray-50 tw-flex tw-items-center tw-justify-center">
        <div className="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-b-2 tw-border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="tw-min-h-screen tw-bg-gray-50 tw-flex tw-flex-col tw-justify-center tw-py-12 sm:tw-px-6 lg:tw-px-8">
      <div className="sm:tw-mx-auto sm:tw-w-full sm:tw-max-w-md">
        <div className="tw-flex tw-justify-center">
          {/* Icône SAV/Support */}
          <svg className="tw-w-16 tw-h-16 tw-text-blue-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z" />
          </svg>
        </div>
        <h2 className="tw-mt-6 tw-text-center tw-text-3xl tw-font-bold tw-tracking-tight tw-text-gray-900">
          Gestion SAV
        </h2>
        <p className="tw-mt-2 tw-text-center tw-text-sm tw-text-gray-600">
          Connectez-vous pour gérer les dossiers SAV
        </p>
      </div>

      <div className="tw-mt-8 sm:tw-mx-auto sm:tw-w-full sm:tw-max-w-md">
        <div className="tw-bg-white tw-py-8 tw-px-4 tw-shadow sm:tw-rounded-lg sm:tw-px-10">
          {error && (
            <div className="tw-mb-4 tw-rounded-md tw-bg-red-50 tw-p-4">
              <div className="tw-flex">
                <div className="tw-flex-shrink-0">
                  <svg className="tw-h-5 tw-w-5 tw-text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="tw-ml-3">
                  <p className="tw-text-sm tw-font-medium tw-text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          <form className="tw-space-y-6" onSubmit={onSubmit}>
            <div>
              <label htmlFor="email" className="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                Adresse email
              </label>
              <div className="tw-mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="tw-block tw-w-full tw-appearance-none tw-rounded-md tw-border tw-border-gray-300 tw-px-3 tw-py-2 tw-placeholder-gray-400 tw-shadow-sm focus:tw-border-blue-500 focus:tw-outline-none focus:tw-ring-blue-500 sm:tw-text-sm"
                  placeholder="vous@exemple.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                Mot de passe
              </label>
              <div className="tw-mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="tw-block tw-w-full tw-appearance-none tw-rounded-md tw-border tw-border-gray-300 tw-px-3 tw-py-2 tw-placeholder-gray-400 tw-shadow-sm focus:tw-border-blue-500 focus:tw-outline-none focus:tw-ring-blue-500 sm:tw-text-sm"
                  placeholder="Votre mot de passe"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="tw-flex tw-w-full tw-justify-center tw-rounded-md tw-border tw-border-transparent tw-bg-blue-600 tw-py-2 tw-px-4 tw-text-sm tw-font-medium tw-text-white tw-shadow-sm hover:tw-bg-blue-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-ring-offset-2 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
              >
                {isLoading ? "Connexion..." : "Se connecter"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
